import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  applyMiniAppTheme,
  applyMiniAppViewport,
  extractInviteTokenFromStartParam,
  getMiniAppInitData,
  hasMiniAppLaunchParams,
  isMiniAppContext,
  waitForMiniAppInitData,
} from '@/lib/tgMiniApp';
import { exchangeTelegramMiniAppInitData } from '@/api/auth';

/** Mini App boot: when the app loads inside a Telegram Mini App
 *  container, we want to skip the /login round-trip and land the
 *  user directly on /usage/dashboard (or /invite/<token> when the
 *  deep link carries `?startapp=invite_…`).
 *
 *  - Non-Mini-App browser: no-op (returns immediately, `ready` stays
 *    true so the rest of the tree mounts as usual).
 *  - Mini App + existing JWT: viewport setup only; routing decisions
 *    are left to the standard PrivateRoutes guard.
 *  - Mini App + no JWT: POST initData → persist JWT → navigate.
 *    On 403 no_binding, fall through to /login so the user sees the
 *    "ask an operator to bind your account" copy.
 *
 *  Returns `{ ready }` — consumers can render a loading placeholder
 *  until the boot exchange settles, avoiding a flash of /login on
 *  the first paint inside the Mini App. */
export function useMiniAppAutoLogin(): { ready: boolean } {
  const navigate = useNavigate();
  // Hold the tree back from a flash of /login when we're a Mini App — decided
  // from the URL launch params, which (unlike the SDK's initData) are present
  // synchronously on first paint even before the async telegram-web-app.js has
  // executed. Plain browser: no launch params → ready=true immediately, no wait.
  const [ready, setReady] = useState<boolean>(
    () => !(isMiniAppContext() || hasMiniAppLaunchParams()),
  );

  useEffect(() => {
    // Not a Mini App at all (no SDK, no launch params) — legacy login handles it.
    if (!isMiniAppContext() && !hasMiniAppLaunchParams()) return;

    let cancelled = false;
    let disposeTheme = () => {};

    (async () => {
      // First open: launch params are in the URL but the async SDK may not have
      // parsed initData yet — wait for it instead of falling through to /login.
      if (!isMiniAppContext()) {
        const ok = await waitForMiniAppInitData();
        if (cancelled) return;
        if (!ok) {
          // Launched as a Mini App but the SDK never delivered a signed
          // initData (blocked/slow telegram.org, or TG preview iframe) — show
          // the login form so the user isn't stuck on a spinner forever.
          setReady(true);
          navigate('/login?reason=miniapp_error', { replace: true });
          return;
        }
      }

      // SDK is ready now — safe to touch window.Telegram.WebApp.
      applyMiniAppViewport();
      disposeTheme = applyMiniAppTheme();

      const inviteToken = extractInviteTokenFromStartParam();
      const existingJwt = localStorage.getItem('jwt');

      if (existingJwt) {
        // Already signed in — route invite_xxx start_params straight to the
        // invite page; otherwise let PrivateRoutes carry on.
        if (inviteToken) navigate(`/invite/${inviteToken}`, { replace: true });
        setReady(true);
        return;
      }

      try {
        const resp = await exchangeTelegramMiniAppInitData({
          init_data: getMiniAppInitData(),
          ...(inviteToken ? { invite_token: inviteToken } : {}),
        });
        if (cancelled) return;
        localStorage.setItem('jwt', resp.data.access);
        if (resp.data.refresh) localStorage.setItem('refresh', resp.data.refresh);
        // Hard navigate so PrivateRoutes / appContext re-evaluate from scratch
        // with the fresh JWT. Destination: invite page when an invite token was
        // carried, else the shift dashboard.
        window.location.replace(inviteToken ? `/invite/${inviteToken}` : '/usage/dashboard');
      } catch (err) {
        if (cancelled) return;
        // Drop the user on /login with a reason hint so LoginPage can render a
        // "your TG isn't bound to an account" banner. Without this hint they'd
        // see the bare login form with no idea why the Mini App didn't sign
        // them in.
        setReady(true);
        const errStatus = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
        const reason =
          errStatus?.status === 403 && errStatus.data?.detail === 'no_binding'
            ? 'miniapp_no_binding'
            : 'miniapp_error';
        const dest = inviteToken
          ? `/login?reason=${reason}&next=${encodeURIComponent(`/invite/${inviteToken}`)}`
          : `/login?reason=${reason}`;
        navigate(dest, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
      disposeTheme();
    };
  }, [navigate]);

  return { ready };
}
